import { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../App";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Sheet, SheetContent } from "../ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  LayoutDashboard,
  Wrench,
  FileText,
  Laptop,
  Menu,
  LogOut,
  User,
  ChevronDown,
  ShoppingBag,
  Home,
  BookOpen,
} from "lucide-react";

const savNav = [
  { name: "Mon espace", href: "/portal", icon: LayoutDashboard },
  { name: "Mes dossiers SAV", href: "/portal/sav", icon: Wrench },
  { name: "Mes devis", href: "/portal/quotes", icon: FileText },
  { name: "Mes appareils", href: "/portal/devices", icon: Laptop },
  { name: "Boutique", href: "/portal/boutique", icon: ShoppingBag },
  { name: "Livres scolaires", href: "/portal/livres", icon: BookOpen },
];

const webNav = [
  { name: "Boutique", href: "/portal/boutique", icon: ShoppingBag },
  { name: "Livres scolaires", href: "/portal/livres", icon: BookOpen },
];

const NavItem = ({ item, onClick }) => (
  <NavLink
    to={item.href}
    end={item.href === "/portal"}
    onClick={onClick}
    className={({ isActive }) =>
      cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
        isActive
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
      )
    }
  >
    <item.icon className="h-5 w-5 flex-shrink-0" />
    <span>{item.name}</span>
  </NavLink>
);

const Sidebar = ({ onClose }) => {
  const { user } = useAuth();
  const isWebClient = user?.account_type === "web";
  const clientNav = isWebClient ? webNav : savNav;

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-28 items-center justify-center px-2 border-b border-border">
        <img src="/logo.png" alt="Micro Multimédia" className="h-24 w-auto object-contain" />
      </div>

      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {clientNav.map((item) => (
            <NavItem key={item.href} item={item} onClick={onClose} />
          ))}
        </nav>
      </ScrollArea>

      <div className="border-t border-border p-4 space-y-3">
        <Link to="/" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors" data-testid="sidebar-home-link">
          <Home className="h-4 w-4" />
          Accueil site
        </Link>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
            {user?.prenom?.[0]}{user?.nom?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.prenom} {user?.nom}</p>
            <p className="text-xs text-muted-foreground">Client</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientPortalLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="client-portal-layout">
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r border-border bg-card lg:block">
        <Sidebar />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <Sidebar onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 px-4 md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-site-link">
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">Accueil site</span>
          </Link>

          <div className="flex-1" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="client-user-menu">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm">
                  {user?.prenom?.[0]}{user?.nom?.[0]}
                </div>
                <span className="hidden md:block">{user?.prenom} {user?.nom}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                Deconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ClientPortalLayout;
